/**
 * fileLifecycle — the create-on-first-edit state machine.
 *
 * A "New mindmap" opens a fresh editor WITHOUT creating a DB row; the row (and
 * the first save) only happen on the first edit. Abandoning it without editing
 * persists nothing. Once a row exists (created just now, or opened from
 * `/f/<id>`), edits autosave. This reducer is the pure decision layer; the hook
 * runs the resulting effect and dispatches back the outcome.
 */

export type FileLifecycleState =
  | { kind: "scratch" }
  // `fileId` is set only when a previous create attempt already materialized
  // the DB row but its first save failed — the retry must reuse that same row.
  | { kind: "pendingNew"; fileId?: string }
  | { kind: "creating"; fileId?: string }
  | { kind: "open"; fileId: string };

export type FileLifecycleEvent =
  | { type: "NEW_MINDMAP" }
  | { type: "OPEN_EXISTING"; fileId: string }
  | { type: "EDIT" }
  | { type: "CREATED"; fileId: string }
  // `fileId` is present when the row was already created before the failure, so
  // the next edit retries the save on the same row instead of orphaning it.
  | { type: "CREATE_FAILED"; fileId?: string };

/** Side effect the caller must run after applying the transition. */
export type FileLifecycleEffect = "none" | "createFile" | "autosave";

export interface FileLifecycleResult {
  state: FileLifecycleState;
  effect: FileLifecycleEffect;
}

export const initialLifecycleState: FileLifecycleState = { kind: "scratch" };

export const fileLifecycleReducer = (
  state: FileLifecycleState,
  event: FileLifecycleEvent,
): FileLifecycleResult => {
  switch (event.type) {
    case "NEW_MINDMAP":
      return { state: { kind: "pendingNew" }, effect: "none" };

    case "OPEN_EXISTING":
      return {
        state: { kind: "open", fileId: event.fileId },
        effect: "none",
      };

    case "EDIT":
      switch (state.kind) {
        case "pendingNew":
          // First edit: materialize the row (title "Untitled") + first PUT.
          // Carry any retained id so a retry reuses the existing row.
          return {
            state: { kind: "creating", fileId: state.fileId },
            effect: "createFile",
          };
        case "open":
          return { state, effect: "autosave" };
        case "creating":
        case "scratch":
        default:
          // Scratch stays local; a creation already in flight is not restarted.
          return { state, effect: "none" };
      }

    case "CREATED":
      // Flush an autosave so edits made during creation are captured.
      return {
        state: { kind: "open", fileId: event.fileId },
        effect: "autosave",
      };

    case "CREATE_FAILED":
      // Revert so the next edit retries creation. If the row was already
      // created (its `fileId` is passed back), retain it so the retry reuses
      // that row instead of minting a new one and orphaning the first.
      return {
        state: { kind: "pendingNew", fileId: event.fileId },
        effect: "none",
      };

    default:
      return { state, effect: "none" };
  }
};
